import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { rhm, apiError, dOnly, titleCase } from "../api";
import { useRhmAuth } from "../RhmAuthContext";
import { Badge, Btn, Card, Empty, Field, Input, Modal, PageHead, Select, Table, Td, Textarea } from "../ui";

const STATUSES = ["new", "contacted", "interested", "follow_up", "converted", "not_interested", "closed"];
const SOURCES = ["website", "whatsapp", "social_media", "referral", "dealer", "call", "other"];
const EMPTY = { name: "", phone: "", email: "", source: "website", status: "new", notes: "", followup_date: "", assigned_to: "" };

export default function RhmLeads() {
    const { can } = useRhmAuth();
    const qc = useQueryClient();
    const [form, setForm] = useState(null);
    const [task, setTask] = useState(null);

    const { data: leads = [], isLoading } = useQuery({
        queryKey: ["rhm-leads"],
        queryFn: async () => (await rhm.get("/leads")).data,
    });
    const { data: followups = [] } = useQuery({
        queryKey: ["rhm-followups"],
        queryFn: async () => (await rhm.get("/followups")).data,
    });

    const invalidate = () => {
        qc.invalidateQueries({ queryKey: ["rhm-leads"] });
        qc.invalidateQueries({ queryKey: ["rhm-followups"] });
    };

    const save = useMutation({
        mutationFn: async (p) => p.id
            ? (await rhm.patch(`/leads/${p.id}`, p)).data
            : (await rhm.post("/leads", { ...p, followup_date: p.followup_date || null })).data,
        onSuccess: () => { toast.success("Lead saved"); setForm(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const setStatus = useMutation({
        mutationFn: async ({ id, status }) => (await rhm.patch(`/leads/${id}`, { status })).data,
        onSuccess: () => { toast.success("Lead updated"); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const saveTask = useMutation({
        mutationFn: async (p) => (await rhm.post("/followups", p)).data,
        onSuccess: () => { toast.success("Follow-up created"); setTask(null); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });
    const closeTask = useMutation({
        mutationFn: async (id) => (await rhm.patch(`/followups/${id}`, { status: "done" })).data,
        onSuccess: () => { toast.success("Follow-up completed"); invalidate(); },
        onError: (e) => toast.error(apiError(e)),
    });

    const tone = (state) => ({ overdue: "danger", today: "gold", upcoming: "muted", done: "good", none: "muted" }[state] || "muted");

    return (
        <div data-testid="rhm-leads-page">
            <PageHead title="Leads & Follow-ups" sub="Enquiries from every channel with reminder tracking."
                action={
                    <div className="flex gap-3">
                        {can("leads.create") && <Btn variant="ghost" onClick={() => setTask({ subject: "", due_date: "", notes: "" })} data-testid="rhm-add-followup">+ FOLLOW-UP</Btn>}
                        {can("leads.create") && <Btn onClick={() => setForm(EMPTY)} data-testid="rhm-add-lead"><Plus className="w-3.5 h-3.5 inline mr-1.5" /> ADD LEAD</Btn>}
                    </div>
                } />

            <Card title={`${leads.length} lead(s)`} className="mb-5">
                {isLoading ? <div className="mono text-xs text-zinc-500">LOADING…</div> : (
                    <Table testId="rhm-leads-table" head={["Lead", "Contact", "Source", "Status", "Follow-up", "Assigned", ""]}
                        empty={!leads.length && <Empty msg="No leads yet." />}>
                        {leads.map((l) => (
                            <tr key={l.id} data-testid={`rhm-lead-row-${l.id}`}>
                                <Td>
                                    <div className="font-semibold">{l.name}</div>
                                    <div className="mono text-[10px] text-zinc-500">{l.lead_number}</div>
                                </Td>
                                <Td className="text-sm text-zinc-400">{l.phone || "—"}<div className="text-xs">{l.email}</div></Td>
                                <Td><Badge>{titleCase(l.source || "")}</Badge></Td>
                                <Td>
                                    {can("leads.edit") ? (
                                        <Select className="!w-36 !py-1.5" value={l.status} data-testid={`lead-status-${l.id}`}
                                            onChange={(e) => setStatus.mutate({ id: l.id, status: e.target.value })} options={STATUSES} />
                                    ) : <Badge>{titleCase(l.status)}</Badge>}
                                </Td>
                                <Td>
                                    {l.followup_date ? (
                                        <Badge tone={tone(l.followup_state)}>{dOnly(l.followup_date)}</Badge>
                                    ) : <span className="text-zinc-600">—</span>}
                                </Td>
                                <Td className="text-sm text-zinc-400">{l.assigned_to || "—"}</Td>
                                <Td>
                                    {can("leads.edit") && (
                                        <button onClick={() => setForm({ ...l, followup_date: (l.followup_date || "").slice(0, 10) })}
                                            className="mono text-[10px] tracking-[0.16em] text-gold" data-testid={`rhm-edit-lead-${l.id}`}>EDIT</button>
                                    )}
                                </Td>
                            </tr>
                        ))}
                    </Table>
                )}
            </Card>

            <Card title={`Follow-up tasks (${followups.length})`}>
                <Table testId="rhm-followups-table" head={["Subject", "Due", "State", "Assigned", "Notes", ""]}
                    empty={!followups.length && <Empty msg="No follow-up tasks." />}>
                    {followups.map((f) => (
                        <tr key={f.id} data-testid={`rhm-followup-row-${f.id}`}>
                            <Td className="font-semibold">{f.subject}</Td>
                            <Td className="text-sm">{dOnly(f.due_date)}</Td>
                            <Td><Badge tone={tone(f.state)}>{String(f.state).toUpperCase()}</Badge></Td>
                            <Td className="text-sm text-zinc-400">{f.assigned_to || f.created_by_name || "—"}</Td>
                            <Td className="text-sm text-zinc-500 max-w-[240px] truncate">{f.notes}</Td>
                            <Td>
                                {f.status !== "done" && can("leads.edit") && (
                                    <button onClick={() => closeTask.mutate(f.id)} data-testid={`followup-done-${f.id}`}
                                        className="mono text-[10px] tracking-[0.16em] text-lime">MARK DONE</button>
                                )}
                            </Td>
                        </tr>
                    ))}
                </Table>
            </Card>

            <Modal open={!!form} onClose={() => setForm(null)} title={form?.id ? "Edit Lead" : "Add Lead"} testId="rhm-lead-modal">
                {form && (
                    <form onSubmit={(e) => { e.preventDefault(); save.mutate(form); }} className="space-y-4">
                        <div className="grid md:grid-cols-2 gap-4">
                            <Field label="Name" required><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="lead-name" /></Field>
                            <Field label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="lead-phone" /></Field>
                            <Field label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
                            <Field label="Source"><Select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} options={SOURCES} data-testid="lead-source" /></Field>
                            <Field label="Status"><Select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUSES} /></Field>
                            <Field label="Follow-up date"><Input type="date" value={form.followup_date || ""} onChange={(e) => setForm({ ...form, followup_date: e.target.value })} data-testid="lead-followup-date" /></Field>
                            <Field label="Assigned to"><Input value={form.assigned_to || ""} onChange={(e) => setForm({ ...form, assigned_to: e.target.value })} /></Field>
                            <Field label="Notes" className="md:col-span-2"><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
                        </div>
                        <Btn type="submit" disabled={save.isPending} data-testid="lead-submit">SAVE LEAD</Btn>
                    </form>
                )}
            </Modal>

            <Modal open={!!task} onClose={() => setTask(null)} title="New Follow-up Task" testId="rhm-followup-modal">
                {task && (
                    <form onSubmit={(e) => { e.preventDefault(); saveTask.mutate(task); }} className="space-y-4">
                        <Field label="Subject" required><Input required value={task.subject} onChange={(e) => setTask({ ...task, subject: e.target.value })} data-testid="followup-subject" /></Field>
                        <Field label="Due date" required><Input type="date" required value={task.due_date} onChange={(e) => setTask({ ...task, due_date: e.target.value })} data-testid="followup-due" /></Field>
                        <Field label="Notes"><Textarea rows={3} value={task.notes} onChange={(e) => setTask({ ...task, notes: e.target.value })} /></Field>
                        <Btn type="submit" disabled={saveTask.isPending} data-testid="followup-submit">CREATE TASK</Btn>
                    </form>
                )}
            </Modal>
        </div>
    );
}
